import type { Contract, GlobalMetadatum, JsonSchemaType } from '@gmn-portal/backend';
import { DateTime } from 'luxon';
import type { TranslateFn } from '@/core/i18n/use-intl';
import { getFieldPermission } from '@/shared/utils/metadata';

/**
 * Gets the display name for a field in activity logs
 * @param field The field path (e.g., 'metadata.key', 'dynamicMetadata.key', 'title', 'status')
 * @param contract The contract document (for dynamic metadata schema)
 * @param globalMetadata The global metadata configuration
 * @param t Translation function
 * @returns The field display name or null if field is deleted/inaccessible
 */
export function getFieldName(
  field: string,
  contract?: Contract,
  globalMetadata?: GlobalMetadatum,
  t?: TranslateFn,
  groupKey?: string | null,
  schemaOverrides?: {
    metadataSchema?: JsonSchemaType;
    dynamicSchema?: JsonSchemaType;
  }
): string | null {
  if (field.startsWith('billingPeriods.')) {
    const [, periodId, subField] = field.split('.');
    const period = (contract as any)?.billingPeriods?.find?.((p: any) => p?.id === periodId);
    const startLabel =
      period?.startDate && DateTime.fromISO(period.startDate).isValid
        ? DateTime.fromISO(period.startDate).toFormat('dd.MM.yyyy')
        : periodId;
    const endLabel =
      period?.endDate && DateTime.fromISO(period.endDate).isValid
        ? DateTime.fromISO(period.endDate).toFormat('dd.MM.yyyy')
        : '';
    const periodLabel = endLabel ? `${startLabel} – ${endLabel}` : startLabel;

    if (!t) return `activity.contract.fields.billingPeriods (${periodLabel})`;

    if (!subField) {
      return `${t('activity.contract.fields.billingPeriods')} (${periodLabel})`;
    }

    const subKey = `activity.contract.billingPeriods.fields.${subField}` as unknown as never;
    const subLabel = t(subKey) as unknown as string;
    return `${t('activity.contract.fields.billingPeriods')} (${periodLabel}) — ${subLabel}`;
  }

  // Handle regular fields
  if (!field.startsWith('metadata.') && !field.startsWith('dynamicMetadata.')) {
    return t
      ? (t(`activity.contract.fields.${field}` as unknown as never) as string)
      : `activity.contract.fields.${field}`;
  }

  // Handle metadata fields
  if (field.startsWith('metadata.')) {
    const [, key] = field.split('.');
    const metadataSchemaToUse =
      schemaOverrides?.metadataSchema ??
      (globalMetadata?.rootContractMetadata as JsonSchemaType | undefined);

    if (!metadataSchemaToUse?.properties?.[key]) {
      return groupKey === 'gmn-admin' ? key : null; // Field has been deleted from schema
    }

    const fieldDef = metadataSchemaToUse.properties[key];
    const permission = getFieldPermission(fieldDef, groupKey ?? null);
    if (!permission.read) return null;

    const description = fieldDef.description;
    return description || key;
  }

  // Handle dynamicMetadata fields
  if (field.startsWith('dynamicMetadata.')) {
    const [, key] = field.split('.');

    const dynamicSchemaToUse =
      schemaOverrides?.dynamicSchema ?? (contract?.dynamicMetadataSchema as JsonSchemaType);

    if (!dynamicSchemaToUse?.properties?.[key]) {
      return groupKey === 'gmn-admin' ? key : null; // Field has been deleted from schema
    }

    const fieldDef = dynamicSchemaToUse.properties[key];
    const permission = getFieldPermission(fieldDef, groupKey ?? null);
    if (!permission.read) return null;

    const description = fieldDef.description;
    return description || key;
  }

  return null;
}
